import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "@src/api/config";
import { showModalSuccess } from "@src/redux/modalTypeSuccess";
import { AppDispatch } from "@src/redux/store";

const urlBase = "api/type_projects";

type RequestParams = Record<string, any>;

export interface TypeProjectPayload {
  name_type_project: string;
  description_type_project: string;
}

export interface TypeProjectsState {
  data: any;
  suggestions: any[];
  current: any | null;
  params: RequestParams;
  meta: any;
  total: number;
}

const defaultPageShape = () => ({
  data: [],
  meta: {
    total: 0,
    last_page: 1
  }
});

const getRows = (payload: any) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const getPageShape = (payload: any) => {
  if (Array.isArray(payload)) {
    return {
      data: payload,
      meta: {
        total: payload.length,
        last_page: 1
      }
    };
  }

  if (Array.isArray(payload?.data) && payload?.meta) return payload;
  if (Array.isArray(payload?.data?.data) && payload?.data?.meta) return payload.data;

  return payload ?? defaultPageShape();
};

const buildSuggestions = (payload: any) => getRows(payload).map((row: any) => ({
    suggestion: row?.name_type_project ?? row?.name ?? ""
  }));

export const getTypeProjects = createAsyncThunk<
  any,
  RequestParams | undefined
>("typeProjects/getAll", async (params = {}) => {
  const response = await api.get(urlBase, { params });
  return {
    params,
    data: response.data
  };
});

export const createTypeProject = createAsyncThunk<
  any,
  TypeProjectPayload,
  { dispatch: AppDispatch }
>("typeProjects/create", async (payload, { dispatch }) => {
  const response = await api.post(urlBase, payload);

  if (response.data.status === "success") {
    await dispatch(showModalSuccess({ message: response.data.message }));
    await dispatch(getTypeProjects());
  }

  return {
    data: response.data
  };
});

export const updateTypeProject = createAsyncThunk<
  any,
  { id: number | string; data: TypeProjectPayload },
  { dispatch: AppDispatch }
>("typeProjects/update", async ({ id, data }, { dispatch }) => {
  const response = await api.put(`${urlBase}/${id}`, data);

  if (response.data.status === "success") {
    await dispatch(showModalSuccess({ message: response.data.message }));
    await dispatch(getTypeProjects());
  }

  return {
    data: response.data
  };
});

export const deleteTypeProject = createAsyncThunk<
  any,
  number | string,
  { dispatch: AppDispatch }
>("typeProjects/delete", async (id, { dispatch }) => {
  const response = await api.delete(`${urlBase}/${id}`);

  if (response.data.status === "success") {
    await dispatch(showModalSuccess({ message: response.data.message }));
    await dispatch(getTypeProjects());
  }

  return {
    data: response.data
  };
});

const initialState: TypeProjectsState = {
  data: defaultPageShape(),
  suggestions: [],
  current: null,
  params: {},
  meta: {},
  total: 0
};

export const typeProjectsSlice = createSlice({
  name: "typeProjects",
  initialState,
  reducers: {
    resetTypeProjects: (state) => {
      state.data = defaultPageShape();
      state.suggestions = [];
      state.current = null;
      state.params = {};
      state.meta = {};
      state.total = 0;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getTypeProjects.fulfilled, (state, action) => {
      const pageShape = getPageShape(action.payload.data);

      state.data = pageShape;
      state.suggestions = buildSuggestions(pageShape);
      state.params = action.payload.params;
      state.meta = pageShape?.meta ?? {};
      state.total = pageShape?.meta?.total ?? getRows(pageShape).length;
    });
  }
});

export const { resetTypeProjects } = typeProjectsSlice.actions;

export default typeProjectsSlice.reducer;
